<template>
    <div class="backdrop" @click.self="onCancel">
        <div class="new-price">
            <div style="display:flex;justify-content:center">Price Override</div>
            <div style="margin-bottom:10px"><hr /></div>
            <div style="display:flex;justify-content:space-between; flex-direction: column;height:100%">
                <!-- Override Pincode -->
                <div class="price-input-container" style>
                    <div style="width:100px;text-align: center;">Pincode</div>
                    <input @focus="clearError" ref="refInput" class="price-input" type="password" v-model="overridePincode" maxlength="6" @keyup="processKeyPress" :disabled="processing" />
                </div>

                <!-- Discounts  -->
                <div class="price-input-container">
                    <div style="margin: 0px 0px 0px 0px;width:100px;text-align: center;">Discount</div>
                    <div style="margin: 0px 5px 0px 0px; ">
                        <select class="price-input administrator-input-item-numeric" v-model="discountPercent" @change="adjustPricePercent">
                            <option value="0">0%</option>
                            <option v-for="i in 100" :key="i" v-bind:value="i">{{ i }}%</option>
                        </select>
                    </div>
                    <div style="margin: 0px 5px 0px 0px; ">
                        <input style="" class="price-input administrator-input-item-numeric" type="number" min="0" :max="originalTotal" step="1" v-model.number="discountAmount" @change="adjustPriceAmount" :disabled="processing" placeholder="$0" />
                    </div>
                </div>

                <!-- Price -->
                <div class="price-input-container">
                    <div style="width:100px;text-align: center;">Price</div>
                    <input
                        style="background-color: rgb(242, 242, 242);"
                        class="price-input administrator-input-item-numeric"
                        type="number"
                        min="-999999"
                        max="999999"
                        step="1"
                        v-model.number="newPrice"
                        @change="adjustFromDirectPrice"
                        @keyup="processKeyPress"
                        :disabled="processing"
                    />
                </div>

                <!-- Note -->
                <div class="price-input-container" style>
                    <div style="width:100px;text-align: center;margin-bottom: 20px;">Note</div>
                    <textarea @focus="clearError" class="price-input" type="text" v-model="note" maxlength="200" @keyup="processKeyPress" style="width:80%" :disabled="processing" />
                </div>

                <!-- Command Bar -->
                <div class="new-price-button-bar" v-if="!processing">
                    <button class="administrator-button new-price-button" @click="onOk" :disabled="processing">
                        Enter
                    </button>
                    <button class="administrator-button button-cancel new-price-button" @click="onCancel" :disabled="processing">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";

export default {
    setup(props, { emit }) {
        const overridePincode = ref("");
        const newPrice = ref(0);
        const note = ref("");
        const refInput = ref(null);
        const originalPrice = ref(0);
        const discountPercent = ref(0);
        const discountAmount = ref(0);

        // The item's tax setup, captured when the modal opens, so a price override can keep its
        // tax consistent instead of leaving the pre-override tax amount stale against a new price.
        // originalTaxAmount stays fixed for the life of the modal - newTaxAmount is what gets
        // recomputed as the cashier adjusts the price.
        const taxInclusive = ref(false);
        const taxRate = ref(0);
        const originalTaxAmount = ref(0);
        const newTaxAmount = ref(0);

        // The customer-facing, tax-inclusive price - what a discount % or $ off should actually be
        // taken from for a tax-inclusive item, not the invisible pre-tax base.
        const originalTotal = computed(() => taxInclusive.value ? (originalPrice.value + originalTaxAmount.value).toRound(2) : originalPrice.value);

        const processing = ref(false);
        var operation = "";

        const initWindow = (pInitPrice, pOperation, pTaxInfo = {}) => {
            overridePincode.value = "";
            note.value = "";
            newPrice.value = pInitPrice.toRound(2);
            originalPrice.value = pInitPrice.toRound(2);
            discountPercent.value = 0;
            discountAmount.value = 0;

            taxInclusive.value = pTaxInfo.taxInclusive ?? false;
            taxRate.value = pTaxInfo.taxRate ?? 0;
            originalTaxAmount.value = pTaxInfo.taxAmount ?? 0;
            newTaxAmount.value = originalTaxAmount.value;

            operation = pOperation;

            setTimeout(function() {
                refInput.value.focus();
            }, 50);
        };

        const onCancel = () => {
            emit("on-cancel");
        };

        const onOk = async () => {
            processing.value = true;
            await operation(overridePincode.value, newPrice.value, newTaxAmount.value, note.value);
            emit("on-cancel");

            processing.value = false;
            overridePincode.value = "";
            note.value = "";
            newPrice.value = 0;
        };

        onMounted(() => {
            processing.value = false;
            refInput.value.focus();
        });

        const processKeyPress = (event) => {
            if (event.keyCode === 13) onOk();
            if (event.keyCode === 27) onCancel();
        };

        // For a tax-inclusive item, splits a tax-inclusive total back into price + tax so the two
        // always add back up to it exactly - the same remainder approach the server uses to derive
        // taxAmount in the first place (total - price), rather than price x rate, which drifts once
        // the price has already been rounded.
        const applyNewTotal = (newTotal) => {
            newTotal = newTotal.toRound(2);
            newPrice.value = (newTotal / (1 + taxRate.value)).toRound(2);
            newTaxAmount.value = (newTotal - newPrice.value).toRound(2);
        };

        const adjustPricePercent = () => {
            discountAmount.value = 0;

            if (taxInclusive.value) {
                applyNewTotal(originalTotal.value - originalTotal.value * (discountPercent.value / 100));
                return;
            }

            newPrice.value = originalPrice.value - originalPrice.value * (discountPercent.value / 100);
            newPrice.value = newPrice.value.toRound(2);
        };
        const adjustPriceAmount = () => {
            discountPercent.value = 0;

            if (taxInclusive.value) {
                applyNewTotal(originalTotal.value - discountAmount.value);
                return;
            }

            newPrice.value = originalPrice.value - discountAmount.value;
            newPrice.value = newPrice.value.toRound(2);
        };

        // A directly-typed price supersedes any % / $ discount selected, and (for a tax-inclusive
        // item) needs its tax recomputed the same way - otherwise it would keep whatever tax was on
        // the price before the edit.
        const adjustFromDirectPrice = () => {
            discountPercent.value = 0;
            discountAmount.value = 0;

            if (taxInclusive.value)
                newTaxAmount.value = ((newPrice.value * (1 + taxRate.value)).toRound(2) - newPrice.value).toRound(2);
        };

        return {
            onCancel,
            refInput,
            onOk,
            processKeyPress,
            initWindow,
            processing,
            overridePincode,
            newPrice,
            note,
            originalPrice,
            originalTotal,
            discountPercent,
            discountAmount,
            adjustPricePercent,
            adjustPriceAmount,
            adjustFromDirectPrice
        };
    }
};
</script>

<style scoped>
.new-price {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 50%;
    left: 50%;
    margin-top: -205px;
    margin-left: -255px;
    width: 500px;
    height: 270px;
    background: var(--main-background);
    border-radius: 10px;
    padding: 30px;
    border: 1px solid var(--card-body-border);
}

.new-price-button-bar {
    display: flex;
    justify-content: flex-end;
    padding: 5px;
    margin: 0px;
}

.new-price-button {
    /* background-color: cornflowerblue; */
    /* color: var(--button-color); */
    padding: 10px;
    margin-right: 10px;
    /* width: 125px;
    cursor: pointer; */
}

.price-input-container {
    display: flex;
    justify-content: flex-start;
    flex-direction: row;
    margin: 10px;
}

.price-input {
    width: 100px;
}
</style>
